// FE-1: QnA-turn render contract.
//
// On a QnA HITL turn the backend re-emits the entire accumulated builder obj_store
// (input-review, party-form, email, ...) in `data.response`, so the generic NDA
// renderer would surface stale "Party Summary"/"Confirm and Continue" controls
// instead of the grounded answer. This selects the answer to render on such a turn,
// ignoring the stale builder siblings.
//
// Author: Raj Kumar <raj.kumar@accellor.com>

const QA_AGENT_ID = "nda_qa_agent";

function hasNonEmptyAnswer(result: unknown): result is { answer: string } {
  if (!result || typeof result !== "object") return false;
  const answer = (result as { answer?: unknown }).answer;
  return typeof answer === "string" && answer.trim().length > 0;
}

function getResponseItems(data: Record<string, unknown>): unknown[] {
  if (Array.isArray(data.response)) return data.response;
  const finalAnswer = data.finalAnswer as Record<string, unknown> | undefined;
  if (finalAnswer && Array.isArray(finalAnswer.response)) return finalAnswer.response;
  return [];
}

/** Fallback text shown when a QnA turn arrives with an empty or missing answer. */
const QA_EMPTY_ANSWER_FALLBACK = "I'm working on your question — please wait a moment.";

/**
 * Returns the grounded QnA answer to render on a QnA turn, or null when this is not a
 * QnA turn. Stale builder items in `response` are ignored — only the nda_qa_agent
 * answer is selected. When it IS a QnA turn but the answer is empty or missing,
 * returns a non-null fallback string so the renderer always shows something meaningful
 * (BUG-27).
 */
export function selectQaAnswerForTurn(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const finalAnswer = record.finalAnswer as Record<string, unknown> | undefined;
  const isQaTurn =
    record.agent_id === QA_AGENT_ID || finalAnswer?.agent_id === QA_AGENT_ID;
  if (!isQaTurn) return null;

  const items = getResponseItems(record);
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index] as { agent_id?: unknown; result?: unknown };
    if (
      item &&
      typeof item === "object" &&
      item.agent_id === QA_AGENT_ID &&
      hasNonEmptyAnswer(item.result)
    ) {
      return item.result.answer;
    }
  }
  // It IS a QnA turn but no valid answer found — return a fallback rather than null
  // so the renderer always shows something instead of a blank bubble.
  return QA_EMPTY_ANSWER_FALLBACK;
}
